'use client';

import { Button } from '@/app/components/ui/Button';
import CustomSelect, { type Option } from '@/app/components/ui/CustomSelect';
import DatePicker from '@/app/components/ui/DatePicker';
import Input from '@/app/components/ui/Input';
import Switch from '@/app/components/ui/Switch';
import TagsInput from '@/app/components/ui/TagsInput';
import Tabs, { TabsList, TabsTrigger } from '@/app/components/ui/Tabs';
import Textarea from '@/app/components/ui/Textarea';
import TimePicker from '@/app/components/ui/TimePicker';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { format } from 'date-fns';
import { Plus, Trash } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { createRule, normalizeValue, pluralizeLabel, resetRuleForField } from './query-utils';
import { getOperatorLabel, isListOperator, isRangeOperator, isUnaryOperator } from './schema';
import type {
  Join,
  QueryDefinition,
  QueryField,
  QueryFieldOption,
  QueryRule,
  QueryValue,
} from './types';
import styles from './QueryBuilder.module.css';

const VALUE_INPUT_DEBOUNCE_MS = 600;
const NO_FOCUS_RING_CLASSNAME = 'focus-visible:ring-0 focus-visible:ring-offset-0';

const toSelectOption = (option: QueryFieldOption): Option => ({
  id: option.value,
  value: option.label,
  meta: { queryFieldOption: option },
});

const toQueryFieldOption = (option: Option): QueryFieldOption =>
  (option.meta?.queryFieldOption as QueryFieldOption | undefined) ?? {
    label: option.value.toString(),
    value: option.id.toString(),
  };

const getOptionItem = (field: QueryField) =>
  field.optionItem ? (option: Option) => field.optionItem?.(toQueryFieldOption(option)) : undefined;

const getTagItem = (field: QueryField) => {
  const render = field.tagItem ?? field.optionItem;
  return render ? (option: Option) => render(toQueryFieldOption(option)) : undefined;
};

export type QueryBuilderProps = {
  fields: QueryField[];
  value: QueryDefinition;
  onChange: (value: QueryDefinition) => void;
  disabled?: boolean;
  compact?: boolean;
};

const createEmptyRule = (join: Join = 'AND'): QueryRule => ({
  id: crypto.randomUUID(),
  join,
  field: '',
  operator: '=',
  value: '',
});

const parseDate = (value: string) => {
  if (!value) return undefined;
  const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const hasInvalidValue = (rule: QueryRule) => {
  if (isUnaryOperator(rule.operator)) return false;
  if (Array.isArray(rule.value)) {
    return rule.value.length === 0 || rule.value.some(value => value === '');
  }
  return rule.value === '' || rule.value == null;
};

const hasRuleContent = (rule: QueryRule) => {
  const hasValue = Array.isArray(rule.value)
    ? rule.value.length > 0
    : rule.value !== '' && rule.value != null;
  return Boolean(rule.field || hasValue || rule.children?.length);
};

export function ValueEditor({
  field,
  rule,
  disabled,
  invalid,
  compact,
  inline,
  compactRelativeDateControls,
  onChange,
  selectPortalContainer,
}: {
  field: QueryField;
  rule: QueryRule;
  disabled?: boolean;
  invalid?: boolean;
  compact?: boolean;
  // Strips the boxed input look (border/background/height) so the control can
  // sit inline as plain text - e.g. inside a QueryFilterSummaryChips chip -
  // while still opening the same dropdown/picker on click.
  inline?: boolean;
  compactRelativeDateControls?: boolean;
  onChange: (value: QueryValue) => void;
  selectPortalContainer?: HTMLElement | null;
}) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [autocompleteValue, setAutocompleteValue] = useState(String(rule.value ?? ''));
  const autocompleteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inlineTriggerClassNames = inline
    ? 'h-full min-h-0 w-auto border-none bg-transparent shadow-none rounded-none px-2 text-xs md:text-xs hover:bg-muted focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0'
    : '';
  const dateTriggerClassNames = `${compact ? 'h-7' : ''} ${inlineTriggerClassNames} ${inline ? 'font-medium' : 'font-normal'} text-foreground disabled:!bg-transparent disabled:!border-input disabled:opacity-50 ${
    invalid ? 'border-destructive ring-destructive/20' : ''
  }`;

  useEffect(() => {
    if (autocompleteTimeoutRef.current) {
      clearTimeout(autocompleteTimeoutRef.current);
      autocompleteTimeoutRef.current = null;
    }
    setAutocompleteValue(String(rule.value ?? ''));
  }, [rule.value]);

  useEffect(
    () => () => {
      if (autocompleteTimeoutRef.current) clearTimeout(autocompleteTimeoutRef.current);
    },
    []
  );

  if (rule.operator === 'inLast') {
    const relative = Array.isArray(rule.value) ? rule.value : ['', 'day'];
    const numberWidthClass = compactRelativeDateControls ? 'w-10' : 'w-14';
    const unitWidthClass = compactRelativeDateControls ? 'w-[90px]' : 'w-28';
    return (
      <div className="flex min-w-0 items-center gap-2">
        <Input
          type="number"
          min={1}
          step={1}
          aria-label="Number"
          placeholder="Number"
          value={relative[0] ?? ''}
          disabled={disabled}
          className={`${NO_FOCUS_RING_CLASSNAME} ${inlineTriggerClassNames} ${numberWidthClass} min-w-0 shrink-0 px-2`}
          onChange={event => onChange([event.target.value, relative[1] ?? 'day'])}
        />
        <CustomSelect
          options={[
            { id: 'day', value: 'Days' },
            { id: 'week', value: 'Weeks' },
            { id: 'month', value: 'Months' },
          ]}
          defaultValue={relative[1] ?? 'day'}
          placeholder="Unit"
          disabled={disabled}
          triggerClassNames={`${inlineTriggerClassNames} ${unitWidthClass} shrink-0 px-2`}
          portalContainer={selectPortalContainer}
          onOptionSelect={option => onChange([relative[0] ?? '', String((option as Option).id)])}
        />
      </div>
    );
  }

  if (isUnaryOperator(rule.operator)) {
    return (
      <span className="flex h-10 items-center text-sm text-muted-foreground">No value needed</span>
    );
  }

  if (isRangeOperator(rule.operator)) {
    const range = Array.isArray(rule.value) ? rule.value : ['', ''];
    if (field.type === 'date' || field.type === 'datetime') {
      const applied: DateRange | undefined = range[0]
        ? { from: parseDate(range[0]), to: parseDate(range[1] ?? '') }
        : undefined;
      return (
        <DatePicker
          mode="range"
          modal
          applied={applied}
          triggerDisabled={disabled}
          disableFutureDates={field.disableFutureDates ?? true}
          triggerClassNames={dateTriggerClassNames}
          iconClassName={inline ? 'hidden' : undefined}
          showShortOptions={false}
          onApply={selected => {
            const next = selected as DateRange | undefined;
            onChange([
              next?.from ? format(next.from, 'yyyy-MM-dd') : '',
              next?.to ? format(next.to, 'yyyy-MM-dd') : '',
            ]);
          }}
          onClear={() => onChange(['', ''])}
        />
      );
    }
    if (field.type === 'duration') {
      return (
        <div className="flex min-w-0 items-center gap-2">
          {[0, 1].map(index => (
            <TimePicker
              key={index}
              mode="duration"
              disabled={disabled}
              secondsValue={range[index] ?? ''}
              onSecondsChange={value =>
                onChange(index === 0 ? [value, range[1] ?? ''] : [range[0] ?? '', value])
              }
              placeholder={index === 0 ? 'From' : 'To'}
              durationLabels={{ clearTime: 'Clear time', minuteSuffix: 'm', secondSuffix: 's' }}
              className="min-w-0 flex-1"
              triggerClassName={`${compact ? 'h-7' : ''} ${inlineTriggerClassNames} focus:ring-inset focus:ring-offset-0`}
            />
          ))}
        </div>
      );
    }
    const type = field.type;
    return (
      <div className="flex min-w-0 items-center gap-2">
        <Input
          type={type}
          value={range[0] ?? ''}
          disabled={disabled}
          aria-invalid={invalid}
          aria-label={`${field.label} range start`}
          className={`${NO_FOCUS_RING_CLASSNAME} ${inlineTriggerClassNames}`}
          onChange={event => onChange([event.target.value, range[1] ?? ''])}
        />
        <span className="text-sm text-muted-foreground">and</span>
        <Input
          type={type}
          value={range[1] ?? ''}
          disabled={disabled}
          aria-invalid={invalid}
          aria-label={`${field.label} range end`}
          className={`${NO_FOCUS_RING_CLASSNAME} ${inlineTriggerClassNames}`}
          onChange={event => onChange([range[0] ?? '', event.target.value])}
        />
      </div>
    );
  }

  if (isListOperator(rule.operator) && (field.options?.length || field.onSearch)) {
    const selected = Array.isArray(rule.value) ? rule.value : [];
    return (
      <CustomSelect
        options={(field.options ?? []).map(toSelectOption)}
        defaultValue={selected}
        multiple
        varient="inline-multiple"
        searchable
        disabled={disabled}
        placeholder="Select values"
        triggerClassNames={`${compact ? 'h-7' : ''} ${inlineTriggerClassNames} ${invalid ? 'border-destructive ring-destructive/20' : ''}`}
        triggerIconClassNames={inline ? 'hidden' : undefined}
        triggerTextRender={
          inline
            ? value => {
                const opts = Array.isArray(value) ? value : value ? [value] : [];
                return opts.length > 1
                  ? `${opts.length} ${pluralizeLabel(field.label)}`
                  : (opts[0]?.value ?? '');
              }
            : undefined
        }
        optionItem={getOptionItem(field)}
        tagItem={getTagItem(field)}
        onSearch={field.onSearch}
        searchLoading={field.searchLoading}
        portalContainer={selectPortalContainer}
        onOptionSelect={options =>
          onChange(((options as Option[] | undefined) ?? []).map(option => String(option.id)))
        }
      />
    );
  }

  if (field.autocomplete) {
    const searchValue = autocompleteValue;
    const normalizedSearch = searchValue.trim().toLowerCase();
    const suggestions = (field.options || [])
      .filter(option => {
        if (!normalizedSearch) return true;
        const targetPage = option.meta?.targetPage as
          | { name?: string; short_name?: string }
          | undefined;
        return [option.label, option.value, targetPage?.name, targetPage?.short_name].some(value =>
          value?.toLowerCase().includes(normalizedSearch)
        );
      })
      .slice(0, 8);

    return (
      <PopoverPrimitive.Root
        open={showSuggestions && suggestions.length > 0}
        onOpenChange={setShowSuggestions}
        modal
      >
        <PopoverPrimitive.Anchor asChild>
          <div className="min-w-0">
            <Input
              value={searchValue}
              disabled={disabled}
              aria-invalid={invalid}
              placeholder={field.placeholder || 'Enter a URL'}
              className={`${NO_FOCUS_RING_CLASSNAME} ${inlineTriggerClassNames}`}
              onChange={event => {
                const nextValue = event.target.value;
                setAutocompleteValue(nextValue);
                setShowSuggestions(true);
                if (autocompleteTimeoutRef.current) clearTimeout(autocompleteTimeoutRef.current);
                autocompleteTimeoutRef.current = setTimeout(
                  () => onChange(nextValue),
                  VALUE_INPUT_DEBOUNCE_MS
                );
              }}
              onFocus={() => setShowSuggestions(true)}
              onClick={() => setShowSuggestions(true)}
            />
          </div>
        </PopoverPrimitive.Anchor>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="start"
            sideOffset={4}
            collisionPadding={8}
            onOpenAutoFocus={event => event.preventDefault()}
            className="z-[70] w-[max(var(--radix-popover-trigger-width),320px)] max-w-[calc(100vw-16px)] max-h-[min(256px,var(--radix-popover-content-available-height))] overflow-y-auto rounded-md border border-border bg-background p-1 shadow-md"
          >
            {suggestions.map(option => (
              <button
                key={option.value}
                type="button"
                className="flex w-full items-center rounded-md px-2 py-2 text-left text-sm hover:bg-accent"
                onMouseDown={event => {
                  event.preventDefault();
                  if (autocompleteTimeoutRef.current) clearTimeout(autocompleteTimeoutRef.current);
                  autocompleteTimeoutRef.current = null;
                  setAutocompleteValue(option.value);
                  onChange(option.value);
                  setShowSuggestions(false);
                }}
              >
                {field.optionItem ? field.optionItem(option) : option.label}
              </button>
            ))}
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    );
  }

  if (isListOperator(rule.operator)) {
    const list = Array.isArray(rule.value) ? rule.value : [];
    return (
      <TagsInput
        initialTags={list.map(String)}
        type={field.type === 'number' ? 'number' : 'text'}
        isNumeric={field.type === 'number'}
        inputProps={{
          disabled,
          placeholder: 'Type a value and press Enter',
          wrapperClassName: '!h-8 !min-h-8 items-center',
          className: '!h-8 !min-h-8 !py-0 leading-8',
        }}
        pillProps={{
          className:
            'h-6 min-w-auto w-auto max-w-full flex items-center overflow-hidden rounded-sm py-0',
        }}
        wrapperClassName={`h-8 min-h-8 max-h-8 items-center overflow-hidden !py-0 focus-within:ring-0 focus-within:ring-offset-0 ${inlineTriggerClassNames} ${invalid ? 'border-destructive ring-destructive/20' : ''}`}
        onTagsChange={onChange}
      />
    );
  }

  if (field.type === 'date' || field.type === 'datetime') {
    return (
      <DatePicker
        mode="single"
        modal
        applyOnSelect
        applied={parseDate(String(rule.value ?? ''))}
        triggerDisabled={disabled}
        disableFutureDates={field.disableFutureDates ?? true}
        triggerClassNames={dateTriggerClassNames}
        iconClassName={inline ? 'hidden' : undefined}
        showTimePicker={field.type === 'datetime'}
        onApply={selected =>
          onChange(
            selected instanceof Date
              ? format(selected, field.type === 'datetime' ? "yyyy-MM-dd'T'HH:mm" : 'yyyy-MM-dd')
              : ''
          )
        }
        onClear={() => onChange('')}
      />
    );
  }

  if (field.type === 'duration') {
    return (
      <TimePicker
        mode="duration"
        disabled={disabled}
        secondsValue={String(rule.value ?? '')}
        onSecondsChange={onChange}
        placeholder={field.placeholder || 'Select time'}
        durationLabels={{ clearTime: 'Clear time', minuteSuffix: 'm', secondSuffix: 's' }}
        className="min-w-0"
        triggerClassName={`${compact ? 'h-7' : ''} ${inlineTriggerClassNames} focus:ring-inset focus:ring-offset-0`}
      />
    );
  }

  if (field.type === 'select' || (field.type === 'number' && !!field.options?.length)) {
    return (
      <CustomSelect
        options={(field.options ?? []).map(toSelectOption)}
        defaultValue={String(rule.value ?? '')}
        disabled={disabled}
        placeholder={field.placeholder || 'Select a value'}
        triggerClassNames={`${compact ? 'h-7' : ''} ${inlineTriggerClassNames} ${invalid ? 'border-destructive ring-destructive/20' : ''}`}
        triggerIconClassNames={inline ? 'hidden' : undefined}
        triggerTextRender={value => {
          const render = getTagItem(field);
          if (!render || !value || Array.isArray(value)) return undefined;
          return render(value);
        }}
        optionItem={getOptionItem(field)}
        searchable
        onSearch={field.onSearch}
        searchLoading={field.searchLoading}
        portalContainer={selectPortalContainer}
        onOptionSelect={option => onChange(String((option as Option | undefined)?.id ?? ''))}
      />
    );
  }

  if (field.type === 'boolean') {
    return (
      <div className="flex h-10 items-center">
        <Switch checked={Boolean(rule.value)} disabled={disabled} onCheckedChange={onChange}>
          {rule.value ? 'Yes' : 'No'}
        </Switch>
      </div>
    );
  }

  if (field.type === 'textarea') {
    return (
      <Textarea
        value={String(rule.value ?? '')}
        disabled={disabled}
        aria-invalid={invalid}
        wrapperClassName={`focus-within:ring-0 focus-within:ring-offset-0 ${inlineTriggerClassNames} ${invalid ? 'border-destructive ring-destructive/20' : ''}`}
        onChange={event => onChange(event.target.value)}
      />
    );
  }

  return (
    <DebouncedTextInput
      type={field.type}
      value={String(rule.value ?? '')}
      disabled={disabled}
      invalid={invalid}
      isNumber={field.type === 'number'}
      placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
      className={inlineTriggerClassNames}
      onChange={onChange}
    />
  );
}

// Debounces the value it emits so a plain text/number query rule doesn't
// trigger a query (and API call) on every keystroke, while the input itself
// stays instantly responsive. Scoped to this one branch on purpose -
// select/date/boolean/etc. fields already only fire on discrete actions.
function DebouncedTextInput({
  type,
  value,
  disabled,
  invalid,
  placeholder,
  isNumber,
  className,
  onChange,
}: {
  type?: string;
  value: string;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
  isNumber?: boolean;
  className?: string;
  onChange: (value: QueryValue) => void;
}) {
  const [typedValue, setTypedValue] = useState(value);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setTypedValue(value);
  }, [value]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <Input
      type={type}
      value={typedValue}
      disabled={disabled}
      aria-invalid={invalid}
      placeholder={placeholder}
      className={`${NO_FOCUS_RING_CLASSNAME} ${className ?? ''}`}
      onChange={event => {
        const next = event.target.value;
        setTypedValue(next);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          onChange(isNumber ? (next === '' ? '' : Number(next)) : next);
        }, VALUE_INPUT_DEBOUNCE_MS);
      }}
    />
  );
}

export default function QueryBuilder({
  fields,
  value,
  onChange,
  disabled,
  compact,
}: QueryBuilderProps) {
  const t = useTranslations('queryBuilder');
  const totalRuleCount = value.groups.reduce((total, group) => total + group.rules.length, 0);
  const hasQueryContent =
    value.groups.length > 1 ||
    totalRuleCount > 1 ||
    value.groups.some(group => group.rules.some(hasRuleContent));

  if (!fields.length) {
    return (
      <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
        Add at least one field to build a query.
      </div>
    );
  }

  const updateRule = (groupId: string, ruleId: string, update: (rule: QueryRule) => QueryRule) => {
    onChange({
      groups: value.groups.map(group =>
        group.id === groupId
          ? { ...group, rules: group.rules.map(rule => (rule.id === ruleId ? update(rule) : rule)) }
          : group
      ),
    });
  };

  return (
    <div className={`${styles.container} ${compact ? 'space-y-2' : 'space-y-4'}`}>
      {value.groups.map((group, groupIndex) => (
        <div key={group.id} className="space-y-3">
          {groupIndex > 0 && (
            <Tabs
              variant="block"
              value={group.join}
              onValueChange={join =>
                onChange({
                  groups: value.groups.map(item =>
                    item.id === group.id ? { ...item, join: join as 'AND' | 'OR' } : item
                  ),
                })
              }
              className="w-fit"
            >
              <TabsList className="h-9">
                <TabsTrigger value="AND" disabled={disabled}>
                  AND
                </TabsTrigger>
                <TabsTrigger value="OR" disabled={disabled}>
                  OR
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}
          <div className={compact ? '' : 'rounded-lg border border-border bg-background p-4'}>
            <div className="space-y-3">
              {group.rules.map((rule, ruleIndex) => {
                const selectedField = fields.find(field => field.name === rule.field);
                const invalid = !compact && hasInvalidValue(rule);
                return (
                  <div
                    key={rule.id}
                    className={
                      compact
                        ? styles.compactRule
                        : 'grid min-w-0 grid-cols-[82px_minmax(0,1fr)_32px] items-start gap-3'
                    }
                  >
                    <div
                      className={`${compact ? `${styles.compactLabel} w-20` : 'w-[82px]'} shrink-0 pt-0.5`}
                    >
                      {ruleIndex === 0 ? (
                        <span className="flex h-7 items-center text-sm text-muted-foreground">
                          Where
                        </span>
                      ) : compact && ruleIndex === 1 ? (
                        <CustomSelect
                          options={[
                            { id: 'AND', value: 'AND' },
                            { id: 'OR', value: 'OR' },
                          ]}
                          defaultValue={rule.join}
                          disabled={disabled}
                          wrapperClassNames="w-full"
                          triggerClassNames="h-7 w-full px-3 text-sm focus:ring-inset focus:ring-offset-0"
                          onOptionSelect={option => {
                            const join = (option as Option | undefined)?.id as Join | undefined;
                            if (!join) return;
                            onChange({
                              groups: value.groups.map(item =>
                                item.id === group.id
                                  ? {
                                      ...item,
                                      rules: item.rules.map((candidate, index) =>
                                        index > 0 ? { ...candidate, join } : candidate
                                      ),
                                    }
                                  : item
                              ),
                            });
                          }}
                        />
                      ) : compact ? (
                        <span className="flex h-7 items-center px-2 text-sm text-muted-foreground">
                          {group.rules[1]?.join || 'AND'}
                        </span>
                      ) : (
                        <Tabs
                          variant="block"
                          value={rule.join}
                          onValueChange={join =>
                            updateRule(group.id, rule.id, item => ({
                              ...item,
                              join: join as 'AND' | 'OR',
                            }))
                          }
                          className="w-fit"
                        >
                          <TabsList className="h-9">
                            <TabsTrigger value="AND" disabled={disabled} className="px-2">
                              AND
                            </TabsTrigger>
                            <TabsTrigger value="OR" disabled={disabled} className="px-2">
                              OR
                            </TabsTrigger>
                          </TabsList>
                        </Tabs>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div
                        className={
                          compact
                            ? `${styles.compactControls} [&_input]:h-7 [&_button]:!border-border [&_input]:!border-border [&_button:focus]:ring-inset [&_button:focus]:ring-offset-0 [&_input:focus]:ring-inset [&_input:focus]:ring-offset-0`
                            : 'grid min-w-0 gap-3 lg:grid-cols-[minmax(180px,0.8fr)_minmax(190px,0.8fr)_minmax(240px,1.4fr)]'
                        }
                      >
                        <CustomSelect
                          options={fields.map(field => ({ id: field.name, value: field.label }))}
                          defaultValue={selectedField?.name || ''}
                          disabled={disabled}
                          searchable
                          placeholder="Select field"
                          triggerClassNames={compact ? 'h-7' : undefined}
                          onOptionSelect={option => {
                            const fieldName = String((option as Option | undefined)?.id ?? '');
                            const nextField = fields.find(field => field.name === fieldName);
                            if (nextField) {
                              updateRule(group.id, rule.id, item =>
                                resetRuleForField(item, nextField)
                              );
                            }
                          }}
                        />
                        <CustomSelect
                          options={(selectedField?.operators || []).map(operator => ({
                            id: operator,
                            value: getOperatorLabel(operator, selectedField?.type),
                          }))}
                          defaultValue={selectedField ? rule.operator : ''}
                          disabled={disabled || !selectedField}
                          placeholder="Operator"
                          triggerClassNames={compact ? 'h-7' : undefined}
                          onOptionSelect={option => {
                            const operator = (option as Option | undefined)?.id as
                              | QueryRule['operator']
                              | undefined;
                            if (operator) {
                              updateRule(group.id, rule.id, item => ({
                                ...item,
                                operator,
                                value: normalizeValue(operator, item.value, selectedField?.type),
                              }));
                            }
                          }}
                        />
                        {selectedField ? (
                          <ValueEditor
                            field={selectedField}
                            rule={rule}
                            disabled={disabled}
                            invalid={invalid}
                            compact={compact}
                            onChange={nextValue =>
                              updateRule(group.id, rule.id, item => ({ ...item, value: nextValue }))
                            }
                          />
                        ) : (
                          <Input disabled placeholder="Select a value" />
                        )}
                      </div>
                      {selectedField?.children?.length ? (
                        <div
                          className={`${styles.childConnector} relative space-y-2 pt-2 before:absolute before:top-0 before:bottom-[14px] before:left-[10px] before:border-l before:border-border`}
                        >
                          {selectedField.children.map((childField, childIndex) => {
                            const childRule = rule.children?.[childIndex] || createRule(childField);
                            const childDisabled = disabled || hasInvalidValue(rule);
                            return (
                              <div
                                key={childField.name}
                                className={`${styles.childControls} [&_input]:h-7 [&_button]:!border-border [&_input]:!border-border [&_button:focus]:ring-inset [&_button:focus]:ring-offset-0 [&_input:focus]:ring-inset [&_input:focus]:ring-offset-0`}
                              >
                                <span
                                  className={`${styles.childConnectorLabel} relative flex h-7 min-w-0 items-center overflow-hidden text-ellipsis whitespace-nowrap pl-8 pr-3 text-sm text-muted-foreground before:absolute before:left-[10px] before:top-1/2 before:w-5 before:border-t before:border-border`}
                                  title={childField.label}
                                >
                                  {childField.label}
                                </span>
                                <CustomSelect
                                  options={childField.operators.map(operator => ({
                                    id: operator,
                                    value:
                                      operator === '='
                                        ? 'equal to'
                                        : getOperatorLabel(operator, childField.type),
                                  }))}
                                  defaultValue={childRule.operator}
                                  disabled={childDisabled}
                                  triggerClassNames="h-7 focus:ring-inset focus:ring-offset-0"
                                  onOptionSelect={option => {
                                    const operator = (option as Option | undefined)?.id as
                                      | QueryRule['operator']
                                      | undefined;
                                    if (!operator) return;
                                    updateRule(group.id, rule.id, item => ({
                                      ...item,
                                      children: (item.children || []).map((child, index) =>
                                        index === childIndex
                                          ? {
                                              ...child,
                                              operator,
                                              value: normalizeValue(
                                                operator,
                                                child.value,
                                                childField.type
                                              ),
                                            }
                                          : child
                                      ),
                                    }));
                                  }}
                                />
                                <div className="min-w-0 [&_button]:!border-border [&_input]:!border-border [&_button:focus]:ring-inset [&_button:focus]:ring-offset-0 [&_input:focus]:ring-inset [&_input:focus]:ring-offset-0">
                                  <ValueEditor
                                    field={childField}
                                    rule={childRule}
                                    disabled={childDisabled}
                                    compact
                                    onChange={nextValue =>
                                      updateRule(group.id, rule.id, item => ({
                                        ...item,
                                        children: (item.children || []).map((child, index) =>
                                          index === childIndex
                                            ? { ...child, value: nextValue }
                                            : child
                                        ),
                                      }))
                                    }
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      className={`${compact ? `${styles.compactDelete} h-7 w-7` : ''} bg-transparent text-muted-foreground hover:bg-transparent hover:text-muted-foreground disabled:!bg-transparent disabled:!text-muted-foreground disabled:opacity-50`}
                      disabled={disabled || (totalRuleCount === 1 && !hasRuleContent(rule))}
                      aria-label="Delete rule"
                      onClick={() => {
                        onChange({
                          groups: value.groups
                            .map(item =>
                              item.id === group.id
                                ? {
                                    ...item,
                                    rules:
                                      totalRuleCount === 1
                                        ? [{ ...createEmptyRule(rule.join), id: rule.id }]
                                        : item.rules.filter(candidate => candidate.id !== rule.id),
                                  }
                                : item
                            )
                            .filter(item => item.rules.length),
                        });
                      }}
                    >
                      <Trash />
                    </Button>
                  </div>
                );
              })}
            </div>
            <div
              className={`${
                compact
                  ? 'mt-3 flex items-center justify-between'
                  : 'mt-4 flex items-center justify-between border-t border-border pt-4'
              } ${compact ? styles.compactActions : ''}`}
            >
              <Button
                type="button"
                variant={compact ? 'ghost' : 'outline'}
                size="sm"
                className={
                  compact
                    ? '-ml-2 h-7 gap-1 bg-transparent pl-1.5 text-primary hover:bg-transparent hover:text-primary disabled:!bg-transparent disabled:!text-primary disabled:opacity-50'
                    : undefined
                }
                disabled={
                  disabled || group.rules.some(rule => !rule.field || hasInvalidValue(rule))
                }
                onClick={() =>
                  onChange({
                    groups: value.groups.map(item =>
                      item.id === group.id
                        ? {
                            ...item,
                            rules: [...item.rules, createEmptyRule(item.rules[1]?.join || 'AND')],
                          }
                        : item
                    ),
                  })
                }
              >
                <Plus /> {compact ? t('addCondition') : t('addRule')}
              </Button>
              {compact && groupIndex === value.groups.length - 1 ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 border-0 bg-transparent text-destructive hover:bg-destructive/10 hover:text-destructive disabled:!bg-transparent disabled:!text-destructive disabled:opacity-50"
                  disabled={disabled || !hasQueryContent}
                  onClick={() =>
                    onChange({
                      groups: [
                        {
                          id: value.groups[0]?.id || crypto.randomUUID(),
                          join: 'AND',
                          rules: [createEmptyRule()],
                        },
                      ],
                    })
                  }
                >
                  {t('clearAllConditions')}
                </Button>
              ) : null}
              {value.groups.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="bg-transparent text-muted-foreground hover:bg-transparent hover:text-muted-foreground disabled:!bg-transparent disabled:!text-muted-foreground disabled:opacity-50"
                  disabled={disabled}
                  onClick={() =>
                    onChange({ groups: value.groups.filter(item => item.id !== group.id) })
                  }
                >
                  <Trash /> Remove group
                </Button>
              )}
            </div>
          </div>
        </div>
      ))}
      {!compact && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() =>
            onChange({
              groups: [
                ...value.groups,
                { id: crypto.randomUUID(), join: 'AND', rules: [createRule(fields[0])] },
              ],
            })
          }
        >
          <Plus /> Add group
        </Button>
      )}
    </div>
  );
}
